import React, { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useHowToVideos } from '../context/HowToVideoContext'
import { urlFor } from '../lib/sanityClient'

const CATEGORY_LABELS = {
  'getting-started': 'Getting Started',
  'booking-scheduling': 'Booking & Scheduling',
  'payments-invoicing': 'Payments & Invoicing',
  'account-settings': 'Account & Settings',
  'jobs-quotes': 'Jobs & Quotes',
  'client-management': 'Client Management',
  'reporting-analytics': 'Reporting & Analytics',
  integrations: 'Integrations',
  troubleshooting: 'Troubleshooting',
}

const DIFFICULTY_CONFIG = {
  beginner: { label: 'Beginner', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  intermediate: { label: 'Intermediate', className: 'bg-amber-50 text-amber-700 border-amber-200' },
  advanced: { label: 'Advanced', className: 'bg-red-50 text-red-700 border-red-200' },
}

function formatDuration(seconds) {
  if (!seconds) return null
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return s > 0 ? `${m}m ${s}s` : `${m}m`
}

function formatDate(iso) {
  if (!iso) return ''
  return new Date(iso).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

function VideoCard({ video, index }) {
  const thumbnailUrl = video.thumbnail
    ? urlFor(video.thumbnail).width(600).height(340).auto('format').fit('crop').url()
    : null

  const duration = formatDuration(video.durationSeconds)
  const difficulty = DIFFICULTY_CONFIG[video.difficulty]

  return (
    <motion.article
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.97 }}
      transition={{ duration: 0.35, delay: index * 0.06 }}
      className="group card overflow-hidden cursor-pointer flex flex-col"
    >
      <Link to={`/how-to-videos/${video.slug}`} className="contents">
        {/* Thumbnail */}
        <div className="relative overflow-hidden bg-surface-2 h-48">
          {thumbnailUrl ? (
            <img
              src={thumbnailUrl}
              alt={video.thumbnail?.alt || video.title}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-accent/10 to-accent/5">
              <svg className="w-12 h-12 text-accent/30" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.069A1 1 0 0121 8.806v6.388a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </div>
          )}

          {/* Play overlay — only shown when a video source exists */}
          {video.hasVideo && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/30 transition-all duration-300">
              <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center opacity-0 group-hover:opacity-100 scale-90 group-hover:scale-100 transition-all duration-300 shadow-lg">
                <svg className="w-5 h-5 text-accent ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z" />
                </svg>
              </div>
            </div>
          )}

          {/* Duration badge */}
          {duration && (
            <span className="absolute bottom-2 right-2 bg-black/70 text-white text-xs font-medium px-2 py-0.5 rounded backdrop-blur-sm">
              {duration}
            </span>
          )}

          {/* Category badge */}
          {video.category && (
            <span className="absolute top-3 left-3 badge bg-white/90 text-accent backdrop-blur-sm text-xs">
              {CATEGORY_LABELS[video.category] || video.category}
            </span>
          )}
        </div>

        {/* Content */}
        <div className="flex flex-col flex-1 p-5">
          {/* Difficulty + Date */}
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            {difficulty && (
              <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border ${difficulty.className}`}>
                {difficulty.label}
              </span>
            )}
            <time className="text-xs text-secondary" dateTime={video.publishedAt}>
              {formatDate(video.publishedAt)}
            </time>
          </div>

          {/* Title */}
          <h2 className="text-base md:text-[15px] font-semibold text-primary leading-snug mb-2 group-hover:text-accent transition-colors duration-200 line-clamp-2">
            {video.title}
          </h2>

          {/* Excerpt */}
          {video.excerpt && (
            <p className="text-secondary text-sm leading-relaxed line-clamp-2 flex-1 mb-3">
              {video.excerpt}
            </p>
          )}

          {/* Tags */}
          {video.tags && video.tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-3">
              {video.tags.slice(0, 3).map((tag) => (
                <span key={tag} className="text-xs text-secondary bg-surface-2 px-2 py-0.5 rounded-full border border-border-subtle">
                  {tag}
                </span>
              ))}
              {video.tags.length > 3 && (
                <span className="text-xs text-secondary">+{video.tags.length - 3}</span>
              )}
            </div>
          )}

          {/* Footer */}
          <div className="flex items-center justify-between pt-3 border-t border-border-subtle mt-auto">
            {video.presenter ? (
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-accent/15 flex items-center justify-center flex-shrink-0">
                  <span className="text-accent text-xs font-semibold">
                    {video.presenter.charAt(0).toUpperCase()}
                  </span>
                </div>
                <span className="text-xs text-secondary truncate max-w-[100px]">{video.presenter}</span>
              </div>
            ) : (
              <span />
            )}
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-accent group-hover:gap-2 transition-all duration-200 flex-shrink-0">
              {video.hasVideo ? 'Watch' : 'Read'}
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5-5 5M6 12h12" />
              </svg>
            </span>
          </div>
        </div>
      </Link>
    </motion.article>
  )
}

export default function HowToVideos() {
  const { videos, loading } = useHowToVideos()
  const [activeCategory, setActiveCategory] = useState('all')
  const [activeTag, setActiveTag] = useState(null)
  const [search, setSearch] = useState('')

  const categories = useMemo(
    () => ['all', ...Array.from(new Set(videos.map((v) => v.category).filter(Boolean)))],
    [videos]
  )

  const allTags = useMemo(
    () => Array.from(new Set(videos.flatMap((v) => v.tags || []))).sort(),
    [videos]
  )

  const filtered = useMemo(() => {
    let result = videos
    if (activeCategory !== 'all') result = result.filter((v) => v.category === activeCategory)
    if (activeTag) result = result.filter((v) => v.tags && v.tags.includes(activeTag))
    if (search.trim()) {
      const q = search.toLowerCase()
      result = result.filter(
        (v) =>
          v.title.toLowerCase().includes(q) ||
          (v.excerpt && v.excerpt.toLowerCase().includes(q)) ||
          (v.tags && v.tags.some((t) => t.toLowerCase().includes(q)))
      )
    }
    return result
  }, [videos, activeCategory, activeTag, search])

  return (
    <main className="min-h-screen bg-surface-0">
      {/* Hero */}
      <section className="pt-32 pb-16 px-4 md:px-8 lg:px-16 bg-surface-1 border-b border-border-subtle">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Link
              to="/support"
              className="inline-flex items-center gap-2 text-sm text-secondary hover:text-primary transition-colors duration-200 cursor-pointer mb-6"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M11 17l-5-5 5-5M6 12h12" />
              </svg>
              Back to Support
            </Link>

            <span className="section-label">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
              </svg>
              How-To Guides
            </span>

            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mt-2">
              <div>
                <h1 className="text-4xl md:text-5xl font-bold text-primary mb-4">
                  Learn Clutch<br />
                  <span className="text-gradient-blue">Step by Step</span>
                </h1>
                <p className="text-secondary text-lg max-w-2xl">
                  Step-by-step guides covering every feature of the Clutch platform — from booking your first job to advanced reporting.
                </p>
              </div>

              {/* Search */}
              <div className="relative w-full sm:w-72 flex-shrink-0">
                <svg
                  className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-secondary pointer-events-none"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 115 11a6 6 0 0112 0z" />
                </svg>
                <input
                  type="text"
                  placeholder="Search guides..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-9 pr-4 py-2.5 text-sm rounded-xl border border-border-medium bg-white text-primary placeholder-secondary focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/20 transition-all duration-200"
                />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="section-padding">
        <div className="section-container">

          {/* Category filter */}
          {!loading && categories.length > 1 && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.1 }}
              className="flex flex-wrap gap-2 mb-5"
            >
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer border ${
                    activeCategory === cat
                      ? 'bg-accent text-white border-accent shadow-glow-sm'
                      : 'bg-surface-card text-secondary border-border-medium hover:border-accent hover:text-accent'
                  }`}
                >
                  {cat === 'all' ? 'All Guides' : (CATEGORY_LABELS[cat] || cat)}
                </button>
              ))}
            </motion.div>
          )}

          {/* Tag filter */}
          {!loading && allTags.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.15 }}
              className="flex flex-wrap gap-2 mb-10"
            >
              {allTags.map((tag) => (
                <button
                  key={tag}
                  onClick={() => setActiveTag(activeTag === tag ? null : tag)}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-all duration-200 cursor-pointer border ${
                    activeTag === tag
                      ? 'bg-accent/15 text-accent border-accent'
                      : 'bg-surface-card text-secondary border-border-subtle hover:border-border-medium hover:text-primary'
                  }`}
                >
                  {tag}
                </button>
              ))}
            </motion.div>
          )}

          {/* Loading skeleton */}
          {loading && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="card overflow-hidden animate-pulse">
                  <div className="h-48 bg-surface-2" />
                  <div className="p-5 space-y-3">
                    <div className="h-3 bg-surface-2 rounded w-1/4" />
                    <div className="h-5 bg-surface-2 rounded w-5/6" />
                    <div className="h-3 bg-surface-2 rounded w-full" />
                    <div className="h-3 bg-surface-2 rounded w-4/5" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Guide grid */}
          {!loading && (
            <>
              {filtered.length === 0 ? (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-center py-20 text-secondary"
                >
                  <svg className="w-12 h-12 mx-auto mb-4 text-border-strong" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.182 16.318A4.486 4.486 0 0012.016 15a4.486 4.486 0 00-3.198 1.318M21 12a9 9 0 11-18 0 9 9 0 0118 0zM9.75 9.75c0 .414-.168.75-.375.75S9 10.164 9 9.75 9.168 9 9.375 9s.375.336.375.75zm-.375 0h.008v.015h-.008V9.75zm5.625 0c0 .414-.168.75-.375.75s-.375-.336-.375-.75.168-.75.375-.75.375.336.375.75zm-.375 0h.008v.015h-.008V9.75z" />
                  </svg>
                  <p className="text-lg font-medium text-primary">No guides found.</p>
                  <p className="text-sm mt-1">Try a different category, tag, or search term.</p>
                  {(activeCategory !== 'all' || activeTag || search) && (
                    <button
                      onClick={() => { setActiveCategory('all'); setActiveTag(null); setSearch('') }}
                      className="mt-4 btn-ghost text-sm py-2 px-4"
                    >
                      Clear filters
                    </button>
                  )}
                </motion.div>
              ) : (
                <>
                  <p className="text-sm text-secondary mb-6">
                    {filtered.length} {filtered.length === 1 ? 'guide' : 'guides'}
                    {activeCategory !== 'all' && ` in ${CATEGORY_LABELS[activeCategory] || activeCategory}`}
                    {activeTag && ` tagged "${activeTag}"`}
                  </p>
                  <motion.div layout className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    <AnimatePresence mode="popLayout">
                      {filtered.map((video, i) => (
                        <VideoCard key={video._id} video={video} index={i} />
                      ))}
                    </AnimatePresence>
                  </motion.div>
                </>
              )}
            </>
          )}
        </div>
      </section>
    </main>
  )
}
